// ** Redux Imports
import { createSlice } from '@reduxjs/toolkit';

export type CurrencyType = 'crypto' | 'fiat';

export type CurrencySettings = {
  code: string;
  decimals?: number;
  name?: string;
  image?: string;
  subdivisions?: number;
  type?: CurrencyType;
  exchangeRate?: number // to USD
};

export interface ConfigReducer {
  currenciesList: string[];
  currentCurrency: string;
  currencySettings: Record<string, CurrencySettings>;
}
const getCurrentCurrency = () => {
  const currency = localStorage.getItem('currentCurrency');
  if (currency) return currency;
  return window.config.defaultCurrency;
};

export const getCurrencySettings = (currency: string): CurrencySettings | undefined => {
  const rawConfig = window.config.currencySettings?.[currency] as Partial<CurrencySettings> | undefined;

  if (!rawConfig) {
    return {
      type: 'fiat',
      code: currency,
    };
}

  // Clone runtime config before normalization; don't want to mutate the original object.
  const config = { ...rawConfig,code: currency } as CurrencySettings;

  if (typeof rawConfig.decimals !== 'undefined') {
    // Ensure decimals is a number and not a string.
    config.decimals = Number(rawConfig.decimals);
    // Build the subdivisions used to convert minor units to display amounts.
    config.subdivisions = Math.pow(10, config.decimals);
  }

  if (typeof rawConfig.type === 'undefined') {
    config.type = 'fiat';
  }

  return config;
};



export const currenciesSlice = createSlice({
  name: 'currencies',
  initialState: <ConfigReducer>{
    currenciesList: window.config.currencies,
    currentCurrency: getCurrentCurrency(),
    currencySettings: window.config?.currencies?.reduce?.((acc, currency) => {
      const settings = getCurrencySettings(currency);
      if (settings) {
        acc[currency] = settings;
      }
      return acc;
    }, {} as Record<string, CurrencySettings>),
  },
  reducers: {
    changeCurrency: (state, action) => {
      /* example */
      state.currentCurrency = action.payload.currency;
      localStorage.setItem('currentCurrency', action.payload.currency);
    },
    receivedCurrencies: (state, action: { payload: Record<string, Partial<CurrencySettings>> }) => {
      const incoming = action.payload || {};

      Object.entries(incoming).forEach(([code, raw]) => {
        const existing = state.currencySettings[code] ?? { code, type: 'fiat' as CurrencyType };
        const merged: CurrencySettings = { ...existing, ...raw, code };

        if (typeof raw.subdivisions !== 'undefined') {
          merged.subdivisions = Number(raw.subdivisions);
          // get decimals from subdivision if not set already
          merged.decimals = merged.decimals ?? Math.log10(merged.subdivisions);
        }

        if (typeof merged.type === 'undefined') {
          merged.type = 'fiat';
        }

        state.currencySettings[code] = merged;
      });
    },
  },
});

export const { changeCurrency, receivedCurrencies } = currenciesSlice.actions;

export default currenciesSlice.reducer;
